import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Typography, Spin, message } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, ArrowLeftOutlined, AppstoreOutlined } from '@ant-design/icons';
import { 
  FullPageContainer,
  PageContainer,
  WelcomeSection,
  EventTypeGrid,
  EventTypeRow,
  EventTypeIcon,
  EventTypeInfo,
  EventTypeTitle,
  EventTypeDescription,
  EventTypeMeta,
  EventTypeButton,
  NoEventsContainer,
  NoEventsIcon,
  NoEventsTitle,
  NoEventsMessage,
  RefreshButton,
  GroupCard,
  GroupEventCount,
  BackButton,
  SlideInView
} from './styled';
import EventTypeService from '../../services/EventTypeService';
import EventTypeGroupService from '../../services/EventTypeGroupService';
import SettingsService from '../../services/SettingsService';
import BookingService from '../../services/BookingService';
import DateTimeSelector from './DateTimeSelector';
import UserInfoForm from './UserInfoForm';
import CustomFormsStep from './CustomFormsStep';
import BookingConfirmation from './BookingConfirmation';
import BookingLookup from './BookingLookup';

const { Title, Paragraph } = Typography;

// Helper function to format duration (accepts hours from backend)
const formatDuration = (hours) => {
  if (!hours) return '';
  const wholeHours = Math.floor(hours);
  const remainingMinutes = Math.round((hours - wholeHours) * 60);
  if (wholeHours >= 1 && remainingMinutes === 0) {
    return `${wholeHours} ${wholeHours === 1 ? 'hour' : 'hours'}`;
  }
  if (wholeHours >= 1) {
    return `${wholeHours} ${wholeHours === 1 ? 'hour' : 'hours'} ${remainingMinutes} min`;
  }
  return `${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}`;
};

const PublicEventBooking = () => {
  const { confirmationCode: urlConfirmationCode } = useParams();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState('eventSelection'); // eventSelection, groupEventSelection, dateTime, userInfo, customForms, confirmation, lookup
  const [groups, setGroups] = useState([]);
  const [ungroupedEventTypes, setUngroupedEventTypes] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [existingBooking, setExistingBooking] = useState(null);
  const [settings, setSettings] = useState({
    welcomeTitle: 'Schedule Your Appointment',
    welcomeMessage: "Welcome to the Black Heart Association's scheduling page! Our mobile units are here to serve your community with free heart health screenings and life-saving education. Select the service you'd like to book below.",
    organizationName: 'Black Heart Association'
  });

  useEffect(() => {
    // If there's a confirmation code in the URL, load that booking
    if (urlConfirmationCode) {
      loadExistingBooking(urlConfirmationCode);
    } else {
      loadEventTypes();
    }
    loadSettings();
  }, [urlConfirmationCode]);

  const loadExistingBooking = async (code) => {
    try {
      setLoading(true);
      const response = await BookingService.getBooking(code);
      
      if (response.success && response.data?.booking) {
        setExistingBooking(response.data.booking);
        setCurrentStep('lookup');
      } else {
        message.error('Booking not found. Please check your confirmation code.');
        navigate('/book-appointment', { replace: true });
        loadEventTypes();
      }
    } catch (error) {
      console.error('Error loading booking:', error);
      message.error('Unable to load booking. Please try again.');
      navigate('/book-appointment', { replace: true });
      loadEventTypes();
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await SettingsService.getAppointmentSettings();
      if (response.success && response.data) {
        setSettings(prevSettings => ({
          ...prevSettings,
          ...response.data
        }));
      } else {
        console.error('Failed to load settings:', response.error);
        // Keep default settings if loading fails
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // Keep default settings if loading fails
    }
  };

  const loadEventTypes = async () => {
    try {
      setLoading(true);
      const [eventTypesRes, groupsRes] = await Promise.all([
        EventTypeService.getPublicEventTypes(),
        EventTypeGroupService.getPublicGroups()
      ]);
      
      let allEventTypes = [];
      if (eventTypesRes.success) {
        allEventTypes = eventTypesRes.data.eventTypes || [];
      } else {
        message.error('Failed to load event types');
      }

      // Process groups and determine which event types are ungrouped
      let loadedGroups = [];
      if (groupsRes.success) {
        loadedGroups = groupsRes.data?.groups || [];
        setGroups(loadedGroups);
      }

      // Collect all event type IDs that are in a group
      const groupedIds = new Set();
      loadedGroups.forEach(group => {
        (group.eventTypes || []).forEach(et => {
          groupedIds.add(et._id);
        });
      });

      // Ungrouped = event types not in any group
      const ungrouped = allEventTypes.filter(et => !groupedIds.has(et._id));
      setUngroupedEventTypes(ungrouped);
    } catch (error) {
      console.error('Error loading event types:', error);
      message.error('Unable to load available events. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleEventTypeSelect = (eventType) => {
    setSelectedEventType(eventType);
    setCurrentStep('dateTime');
  };

  const handleGroupSelect = (group) => {
    // If group has exactly one event type, skip the sub-selection
    if (group.eventTypes && group.eventTypes.length === 1) {
      setSelectedGroup(group);
      setSelectedEventType(group.eventTypes[0]);
      setCurrentStep('dateTime');
    } else {
      setSelectedGroup(group);
      setCurrentStep('groupEventSelection');
    }
  };

  const handleBackToGroups = () => {
    setSelectedGroup(null);
    setCurrentStep('eventSelection');
  };

  const handleTimeSlotSelect = (timeSlot) => {
    setSelectedTimeSlot(timeSlot);
    setCurrentStep('userInfo');
  };

  const handleBookingSubmit = (submittedUserInfo) => {
    setUserInfo(submittedUserInfo);
    
    // Debug logging to check associatedForms
    console.log('Selected event type:', selectedEventType);
    console.log('Associated forms:', selectedEventType.associatedForms);
    console.log('Has forms?', selectedEventType.associatedForms && selectedEventType.associatedForms.length > 0);
    
    // Check if there are associated forms to fill out
    if (selectedEventType.associatedForms && selectedEventType.associatedForms.length > 0) {
      setCurrentStep('customForms');
    } else {
      setBookingData({
        eventType: selectedEventType,
        timeSlot: selectedTimeSlot,
        userInfo: submittedUserInfo,
        formResponses: {}
      });
      setCurrentStep('confirmation');
    }
  };

  const handleFormsSubmit = (responses) => {
    setBookingData({
      eventType: selectedEventType,
      timeSlot: selectedTimeSlot,
      userInfo,
      formResponses: responses
    });
    setCurrentStep('confirmation');
  };

  const handleBackToUserInfo = () => {
    setCurrentStep('userInfo');
  };

  const handleBackToEventSelection = () => {
    setSelectedEventType(null);
    setSelectedTimeSlot(null);
    setSelectedGroup(null);
    setExistingBooking(null);
    setCurrentStep('eventSelection');
    // Clear the confirmation code from URL if present
    if (urlConfirmationCode) {
      navigate('/book-appointment', { replace: true });
    }
  };

  const handleBackToDateTime = () => {
    setSelectedTimeSlot(null);
    setCurrentStep('dateTime');
  };

  // Callback when booking is confirmed - update URL with confirmation code
  const handleBookingConfirmed = (code) => {
    navigate(`/book-appointment/${code}`, { replace: true });
  };

  // Callback when booking is cancelled from lookup view
  const handleBookingCancelled = () => {
    setExistingBooking(null);
    navigate('/book-appointment', { replace: true });
    loadEventTypes();
    setCurrentStep('eventSelection');
  };

  const renderWelcomeSection = () => (
    <WelcomeSection>
      <Row justify="center">
        <Col xs={24} md={16} lg={12}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <img 
              src="/bha-original-logo.png" 
              alt={settings?.organizationName || 'Black Heart Association'} 
              style={{ height: '60px', marginBottom: '16px' }}
            />
            <Title level={2} style={{ margin: '0 0 16px 0', color: '#1a1a1a' }}>
              {settings?.welcomeTitle || 'Schedule Your Appointment'}
            </Title>
            <Paragraph style={{ fontSize: '16px', color: '#666', margin: 0 }}>
              {settings?.welcomeMessage || "Welcome to the Black Heart Association's scheduling page! Our mobile units are here to serve your community with free heart health screenings and life-saving education. Select the service you'd like to book below."}
            </Paragraph>
          </div>
        </Col>
      </Row>
    </WelcomeSection>
  );

  const renderEventTypeCard = (eventType, index) => (
    <EventTypeRow 
      key={eventType._id}
      onClick={() => handleEventTypeSelect(eventType)}
      style={{
        cursor: 'pointer',
        backgroundColor: '#fff',
        '--entrance-delay': `${index * 0.06}s`
      }}
    >
      <EventTypeIcon 
        style={{ 
          backgroundColor: eventType.color + '20',
          color: eventType.color 
        }}
      >
        <CalendarOutlined />
      </EventTypeIcon>
      
      <EventTypeInfo>
        <EventTypeTitle level={4} style={{ color: '#1a1a1a' }}>
          {eventType.name}
        </EventTypeTitle>
        
        <EventTypeDescription style={{ color: '#666' }}>
          {eventType.description}
        </EventTypeDescription>
        
        <EventTypeMeta>
          <div className="meta-item">
            <ClockCircleOutlined />
            <span>{formatDuration(eventType.duration)}</span>
          </div>
        </EventTypeMeta>
      </EventTypeInfo>
      
      <EventTypeButton 
        type="primary" 
        size="large"
        style={{ 
          backgroundColor: eventType.color, 
          borderColor: eventType.color,
          color: '#fff'
        }}
      >
        Book Now
      </EventTypeButton>
    </EventTypeRow>
  );

  const renderGroupCard = (group, index) => (
    <GroupCard
      key={group._id}
      onClick={() => handleGroupSelect(group)}
      style={{ '--entrance-delay': `${index * 0.06}s` }}
    >
      <EventTypeIcon 
        style={{ 
          backgroundColor: (group.color || '#1890ff') + '20',
          color: group.color || '#1890ff'
        }}
      >
        <AppstoreOutlined />
      </EventTypeIcon>
      
      <EventTypeInfo>
        <EventTypeTitle level={4} style={{ color: '#1a1a1a' }}>
          {group.name}
        </EventTypeTitle>
        
        {group.description && (
          <EventTypeDescription style={{ color: '#666' }}>
            {group.description}
          </EventTypeDescription>
        )}
        
        <EventTypeMeta>
          <GroupEventCount>
            <CalendarOutlined />
            <span>{group.eventTypes?.length || 0} option{(group.eventTypes?.length || 0) !== 1 ? 's' : ''} available</span>
          </GroupEventCount>
        </EventTypeMeta>
      </EventTypeInfo>
      
      <EventTypeButton 
        type="primary" 
        size="large"
        style={{ 
          backgroundColor: group.color || '#1890ff', 
          borderColor: group.color || '#1890ff',
          color: '#fff'
        }}
      >
        View Options
      </EventTypeButton>
    </GroupCard>
  );

  const renderGroupEventSelection = () => {
    if (!selectedGroup) return null;

    return (
      <FullPageContainer>
        {renderWelcomeSection()}
        <PageContainer>
          <SlideInView $direction="right" key={selectedGroup._id}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
              <BackButton onClick={handleBackToGroups}>
                <ArrowLeftOutlined />
                Back to all services
              </BackButton>

              <div style={{ marginBottom: '24px' }}>
                <Typography.Title level={3} style={{ margin: '0 0 8px 0', color: '#1a1a1a' }}>
                  {selectedGroup.name}
                </Typography.Title>
                {selectedGroup.description && (
                  <Typography.Paragraph style={{ color: '#666', margin: 0, fontSize: '15px' }}>
                    {selectedGroup.description}
                  </Typography.Paragraph>
                )}
              </div>

              <EventTypeGrid>
                {(selectedGroup.eventTypes || []).map(renderEventTypeCard)}
              </EventTypeGrid>
            </div>
          </SlideInView>
        </PageContainer>
      </FullPageContainer>
    );
  };

  const hasGroups = groups.length > 0;
  const hasUngrouped = ungroupedEventTypes.length > 0;
  const hasContent = hasGroups || hasUngrouped;

  const renderEventTypeSelection = () => (
    <FullPageContainer>
      {renderWelcomeSection()}
      
      <PageContainer>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px', color: '#666' }}>Loading available appointments...</div>
          </div>
        ) : hasContent ? (
          <EventTypeGrid>
            {/* Render group cards first */}
            {groups.map(renderGroupCard)}
            
            {/* Render ungrouped event types */}
            {ungroupedEventTypes.map((et, i) => renderEventTypeCard(et, groups.length + i))}
          </EventTypeGrid>
        ) : (
          <NoEventsContainer>
            <NoEventsIcon>
              <CalendarOutlined />
            </NoEventsIcon>
            <NoEventsTitle level={3}>
              No Appointments Available
            </NoEventsTitle>
            <NoEventsMessage>
              We don&apos;t have any appointment slots available at this time. 
              <br />
              Please check back soon or contact us directly for assistance.
            </NoEventsMessage>
            <RefreshButton onClick={loadEventTypes}>
              Refresh Page
            </RefreshButton>
          </NoEventsContainer>
        )}
      </PageContainer>
    </FullPageContainer>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'eventSelection':
        return renderEventTypeSelection();
      case 'groupEventSelection':
        return renderGroupEventSelection();
      case 'dateTime':
        return (
          <FullPageContainer>
            <SlideInView $direction="right" key="dateTime">
              <DateTimeSelector
                eventType={selectedEventType}
                onTimeSlotSelect={handleTimeSlotSelect}
                onBack={handleBackToEventSelection}
              />
            </SlideInView>
          </FullPageContainer>
        );
      case 'userInfo':
        return (
          <FullPageContainer>
            <SlideInView $direction="right" key="userInfo">
              <UserInfoForm
                eventType={selectedEventType}
                timeSlot={selectedTimeSlot}
                onSubmit={handleBookingSubmit}
                onBack={handleBackToDateTime}
              />
            </SlideInView>
          </FullPageContainer>
        );
      case 'customForms':
        return (
          <FullPageContainer>
            <SlideInView $direction="right" key="customForms">
              <CustomFormsStep
                eventType={selectedEventType}
                timeSlot={selectedTimeSlot}
                userInfo={userInfo}
                onSubmit={handleFormsSubmit}
                onBack={handleBackToUserInfo}
              />
            </SlideInView>
          </FullPageContainer>
        );
      case 'confirmation':
        return (
          <FullPageContainer>
            <SlideInView $direction="right" key="confirmation">
              <BookingConfirmation
                bookingData={bookingData}
                onNewBooking={handleBackToEventSelection}
                onBack={handleBackToDateTime}
                onBookingConfirmed={handleBookingConfirmed}
              />
            </SlideInView>
          </FullPageContainer>
        );
      case 'lookup':
        return (
          <FullPageContainer>
            <SlideInView $direction="right" key="lookup">
              <BookingLookup
                booking={existingBooking}
                onNewBooking={handleBackToEventSelection}
                onBookingCancelled={handleBookingCancelled}
              />
            </SlideInView>
          </FullPageContainer>
        );
      default:
        return renderEventTypeSelection();
    }
  };

  return renderCurrentStep();
};

export default PublicEventBooking;
